import React, { useContext, useEffect, useRef, useState } from 'react';
import classes from './TypewritterAnimation.module.scss';
import { CSSTransition } from 'react-transition-group';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type TypewritterAnimationViewProps = {
  //
  content: string;
  learnMoreArticle: string | null;
  learnMoreText: string | null;
  duration?: number;
};

export const TypewritterAnimationView = (props: TypewritterAnimationViewProps): JSX.Element => {
  const { content, learnMoreArticle, duration = 2000, learnMoreText } = props;
  const { beacon } = useContext(BeaconContext);
  const [phase, setPhase] = useState<'display' | 'fadeOut' | 'typing'>('display');
  const [fullContent, setFullContent] = useState(content);
  const [displayedText, setDisplayedText] = useState(content);
  const cssRef = useRef<HTMLParagraphElement>(null);

  const handleBeacon = () => {
    learnMoreArticle && beacon('article', learnMoreArticle, { type: 'modal' });
  };

  // When the content prop changes and differs from what we have, start fading out.
  useEffect(() => {
    if (content !== fullContent) {
      setPhase('fadeOut');
    }
  }, [content, fullContent]);

  // When fade-out completes, update the text and start the typewriter animation.
  const handleExited = () => {
    setFullContent(content);
    setDisplayedText('');
    setPhase('typing');
  };

  // Typewriter effect: incrementally reveal new content.
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (phase === 'typing') {
      let index = 0;
      const intervalTime = fullContent.length > 0 ? duration / fullContent.length : duration;
      interval = setInterval(() => {
        setDisplayedText(() => {
          return fullContent.slice(0, index + 1);
        });
        index++;
        if (index >= fullContent.length) {
          clearInterval(interval);
          setPhase('display');
        }
      }, intervalTime);
    }
    return () => {
      clearInterval(interval);
    };
  }, [phase, fullContent, duration]);

  return (
    <CSSTransition nodeRef={cssRef} in={phase !== 'fadeOut'} timeout={300} classNames="fade" onExited={handleExited}>
      <p className="no-b-m relative" ref={cssRef}>
        {/* Hidden element to preserve container height */}
        <span className={classes.HiddenText}>
          {fullContent} {learnMoreArticle && <span className={classes.LearnMore}>{learnMoreText}</span>}
        </span>
        {/* Visible typewriter text overlay */}
        <span className={classes.TextBox}>
          {displayedText}{' '}
          {displayedText === fullContent && learnMoreArticle && (
            <span className={classes.LearnMore} onClick={handleBeacon}>
              {learnMoreText}
            </span>
          )}
        </span>
      </p>
    </CSSTransition>
  );
};
